import dJSON from 'dirty-json';
import Splide from '@splidejs/splide';

const moduleSelector = '.project-gallery';

const arrowSvg = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="26" viewBox="0 0 16 26" fill="none" aria-hidden="true"><path d="M15.1399 12.0455C15.5952 12.4439 15.5952 13.1523 15.1399 13.5507L1.6585 25.3469C1.01192 25.9127 0 25.4535 0 24.5943L0 1.00186C0 0.142704 1.01192 -0.316473 1.6585 0.249287L15.1399 12.0455Z" fill="#00B3CA"/></svg>';
const arrowSvgPrev = arrowSvg.replace('<svg ', '<svg style="transform:rotate(180deg)" ');

(() => {
  const modules = document.querySelectorAll(moduleSelector);
  if (!modules.length) return;

  modules.forEach((section) => {
    try {
      section.querySelector('script')?.remove();
      const { data } = dJSON.parse(section.data);
      if (!Array.isArray(data) || !data.length) return;

      const navEl = section.querySelector('.project-gallery__nav');
      const navList = navEl.querySelector('.splide__list');
      const contentArea = section.querySelector('.project-gallery__content');
      const lightbox = section.querySelector('.project-gallery__lightbox');

      if (!navEl || !navList || !contentArea || !lightbox) return;

      const slugify = (str) => (str || 'project')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');

      const getImageSrc = (img) => img?.url ?? (typeof img === 'string' ? img : '');

      // --- Preload all images into browser cache ---
      const preloaded = new Set();
      const preloadImg = (src) => {
        if (!src || preloaded.has(src)) return;
        preloaded.add(src);
        const img = new Image();
        img.src = src;
      };

      data.forEach((project) => {
        const logo = project.project_logo?.url
          ?? (typeof project.project_logo === 'string' ? project.project_logo : '');
        if (logo) preloadImg(logo);
        (project.project_images || []).forEach((img) => preloadImg(getImageSrc(img)));
      });

      // --- Populate nav slides from data ---
      const navSlides = [];

      data.forEach((item, i) => {
        const firstImg = item.project_images?.[0];
        const imgSrc = firstImg?.url ?? (typeof firstImg === 'string' ? firstImg : '');

        const slide = document.createElement('li');
        slide.className = 'splide__slide';
        if (i === 0) slide.classList.add('is-nav-active');

        const slug = slugify(item.project_name);

        slide.innerHTML = /* html */ `
          <a href="#${slug}" class="project-gallery__nav__item" data-index="${i}">
            ${imgSrc ? `<img src="${imgSrc}" alt="${item.project_name || ''}" loading="lazy">` : ''}
            <span class="project-gallery__nav__title">${item.project_name || ''}</span>
          </a>
        `;

        navList.appendChild(slide);
        navSlides.push(slide);
      });

      // --- Lightbox logic ---
      let lbMain = null;
      let lbThumbs = null;

      const getValidImages = (projectIndex) => (data[projectIndex]?.project_images || [])
        .map((img) => getImageSrc(img))
        .filter(Boolean);

      const openLightbox = (projectIndex, imageIndex) => {
        const images = getValidImages(projectIndex);
        if (!images.length) return;

        const mainList = lightbox.querySelector('.project-gallery__lightbox__main .splide__list');
        const thumbList = lightbox.querySelector('.project-gallery__lightbox__thumbs .splide__list');
        mainList.innerHTML = '';
        thumbList.innerHTML = '';

        images.forEach((src) => {
          const mainSlide = document.createElement('li');
          mainSlide.className = 'splide__slide';
          mainSlide.innerHTML = `<img src="${src}" alt="" role="presentation" aria-hidden="true" loading="lazy">`;
          mainList.appendChild(mainSlide);

          const thumbSlide = document.createElement('li');
          thumbSlide.className = 'splide__slide';
          thumbSlide.innerHTML = `<img src="${src}" alt="" role="presentation" aria-hidden="true" loading="lazy">`;
          thumbList.appendChild(thumbSlide);
        });

        if (lbMain) lbMain.destroy();
        if (lbThumbs) lbThumbs.destroy();

        const mainEl = lightbox.querySelector('.project-gallery__lightbox__main');
        const thumbsEl = lightbox.querySelector('.project-gallery__lightbox__thumbs');

        lbMain = new Splide(mainEl, {
          type: 'slide',
          rewind: true,
          perPage: 1,
          arrows: true,
          pagination: false,
          start: imageIndex,
          speed: 300,

        });

        lbThumbs = new Splide(thumbsEl, {
          type: 'slide',
          rewind: true,
          perPage: 6,
          perMove: 1,
          height: 125,
          gap: 8,
          arrows: false,
          pagination: true,
          start: imageIndex,
          speed: 300,
          isNavigation: true,
          breakpoints: {
            768: {
              perPage: 5,
              height: 60,
            },
            480: { perPage: 3 },
          },
        });

        lightbox.classList.add('is-open');
        document.body.style.overflow = 'hidden';

        lbMain.sync(lbThumbs);
        lbMain.mount();
        lbThumbs.mount();
      };

      const closeLightbox = () => {
        lightbox.classList.remove('is-open');
        document.body.style.overflow = '';
        if (lbMain) { lbMain.destroy(); lbMain = null; }
        if (lbThumbs) { lbThumbs.destroy(); lbThumbs = null; }
      };

      lightbox.querySelector('.project-gallery__lightbox__close').addEventListener('click', closeLightbox);

      lightbox.addEventListener('click', (e) => {
        if (e.target === lightbox) closeLightbox();
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && lightbox.classList.contains('is-open')) closeLightbox();
      });

      // --- Render project content by index ---
      const renderContent = (index) => {
        const project = data[index];
        if (!project) return;

        const logoSrc = project.project_logo?.url
          ?? (typeof project.project_logo === 'string' ? project.project_logo : '');
        const images = getValidImages(index);

        contentArea.innerHTML = /* html */ `
          <h2 class="project-gallery__content__title">${project.project_name || ''}</h2>
          ${logoSrc ? `<div class="project-gallery__content__logo"><img src="${logoSrc}" alt="${project.project_name || ''}" loading="lazy"></div>` : ''}
          <div class="project-gallery__content__grid">
            ${images.map((src, i) => `<div class="project-gallery__content__grid__item" data-index="${i}"><img src="${src}" alt="" loading="lazy" role="presentation" aria-hidden="true"></div>`).join('')}
          </div>
        `;

        contentArea.querySelectorAll('.project-gallery__content__grid__item').forEach((item) => {
          item.addEventListener('click', () => {
            openLightbox(index, parseInt(item.dataset.index, 10));
          });
        });
      };

      // --- Select a project ---
      let currentIndex = -1;
      let swapTimer = null;

      const selectProject = (index) => {
        if (index === currentIndex) return;
        currentIndex = index;
        navSlides.forEach((s, i) => s.classList.toggle('is-nav-active', i === index));

        if (swapTimer) clearTimeout(swapTimer);

        const isFirstLoad = !contentArea.innerHTML.trim();

        if (isFirstLoad) {
          contentArea.id = slugify(data[index]?.project_name);
          renderContent(index);
          contentArea.classList.add('is-visible');
          return;
        }

        contentArea.classList.remove('is-visible');

        swapTimer = setTimeout(() => {
          if (index !== currentIndex) return;
          contentArea.id = slugify(data[index]?.project_name);
          renderContent(index);
          requestAnimationFrame(() => contentArea.classList.add('is-visible'));
        }, 300);
      };

      // --- Mount Nav Splide ---
      const navSplide = new Splide(navEl, {
        type: 'loop',
        // rewind: true,
        height: 225,
        perPage: 6,
        perMove: 1,
        gap: 3,
        arrows: true,
        pagination: false,
        speed: 300,
        breakpoints: {
          1400: { perPage: 5 },
          1200: { perPage: 4 },
          992: { perPage: 3 },
          768: { perPage: 2 },
          480: { perPage: 1 },
        },
      });

      navSplide.mount();

      // Arrow icons
      const setArrowIcons = () => {
        const prevBtn = navEl.querySelector('.splide__arrow--prev');
        const nextBtn = navEl.querySelector('.splide__arrow--next');
        if (prevBtn) prevBtn.innerHTML = arrowSvgPrev;
        if (nextBtn) nextBtn.innerHTML = arrowSvg;
      };

      setArrowIcons();
      navSplide.on('resize updated', () => requestAnimationFrame(setArrowIcons));

      navSplide.on('moved', (newIndex) => selectProject(newIndex));

      // Delegated click handles originals and Splide clones
      navEl.addEventListener('click', (e) => {
        const anchor = e.target.closest('.project-gallery__nav__item');
        if (!anchor) return;
        e.preventDefault();
        const index = parseInt(anchor.dataset.index, 10);
        if (Number.isNaN(index)) return;
        navSplide.go(index);
        contentArea.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });

      selectProject(0);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`Error: ${moduleSelector}`, error);
    }
  });
})();
